import React from 'react';
import { Stack, Text } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Routes } from '~/domains/common/constants/routes';
import { SegmentedControl } from '~/domains/common/components/SegmentedControl/SegmentedControl';
import { AppDownloadPrompt } from '~/domains/common/components/AppDownloadPrompt';
import { NewToSplash } from '~/domains/common/components/NewToSplash/NewToSplash';
import { ConnectedSignUpModal } from '~/domains/common/components/SignUpModal/ConnectedSignUpModal';
import Posts from '~/domains/commissioner/common/Posts/Posts';
import dt from '~/testing';
import Modal from '~/domains/common/components/Modal/Modal/Modal';
import { getHandle } from '~/domains/user/utils/getHandle';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';

import { Section, SectionContent, SectionTitle } from '../Sections/components';
import { ContestHeader } from '../ContestHeader/ContestHeader';
import Entrants from '../Entrants/Entrants';
import MyEntries from '../Sections/MyEntries/MyEntries';
import AboutSection from '../Sections/About/About';
import PrizesSection from '../Sections/Prizes/Prizes';
import RulesSection from '../Sections/Rules';
import ScheduleSection from '../Sections/Schedule/Schedule';
import LongRules from '../Sections/LongRules';

import useStyles from './useStyles';
import useContestDetail, { ContentTab } from './useContestDetail';

type ContestDetailProps = {
  isSidePanel?: boolean;
  hideEntries?: boolean;
  hideCTA?: boolean;
};

function ContestDetail({ isSidePanel, hideEntries, hideCTA }: ContestDetailProps) {
  const { classes } = useStyles({ isSidePanel });
  const { t } = useTranslation('contest');
  const {
    activeTab,
    channel,
    contestData,
    handleDownloadClick,
    isAllowedInLocation,
    isAppPromptModalShown,
    isMobile,
    isTabsShown,
    maxEntriesPerUser,
    myEntriesStatus,
    openEntryNameEditModal,
    setActiveTab,
    setShowAppPromptModal,
    tabs,
    user,
  } = useContestDetail({ isSidePanel, hideEntries });
  const isTablet = useIsTablet();

  const isEntertainmentOnly = contestData.settings?.entertainmentOnlySettings?.enabled;

  return (
    <>
      {isAppPromptModalShown && (
        <Modal
          onClose={() => setShowAppPromptModal(false)}
          isOpened={isAppPromptModalShown}
          withCloseButton={false}
          closeOnClickOutside={false}
          size="xl"
        >
          <AppDownloadPrompt
            title={t('detail.appPromo.title')}
            subtitle={t('detail.appPromo.subtitle')}
            onDownloadClick={() => handleDownloadClick()}
            onContinueClick={() => {
              setShowAppPromptModal(false);
            }}
            isMobile={isMobile}
            contest={contestData}
          />
        </Modal>
      )}
      <ContestHeader
        contestId={contestData.id}
        isAllowedInLocation={isAllowedInLocation}
        isSidePanel={isSidePanel}
        hideCTA={hideCTA}
      />
      <section className={classes.contentContainer}>
        <div className={classes.content}>
          <div className={classes.main}>
            {user && isTabsShown ? (
              <SegmentedControl
                value={activeTab}
                onChange={(v) => {
                  setActiveTab(v as ContentTab);
                }}
                data={tabs.map(({ label, value }) => ({
                  value,
                  label,
                }))}
                w="100%"
                mb="lg"
              />
            ) : null}
            <Stack spacing="xl">
              {!user && isTablet && <NewToSplash />}
              {!isTabsShown || activeTab === ContentTab.DETAILS ? (
                <>
                  {contestData?.description ? (
                    <SectionContent>
                      <Text
                        data-test-id={dt.contestDetail.page.decription}
                        m={0}
                        component="p"
                        className={classes.description}
                      >
                        {contestData.description}
                      </Text>
                    </SectionContent>
                  ) : null}
                  {user && !isSidePanel && !hideEntries ? (
                    <MyEntries
                      contestId={contestData.id}
                      myEntriesStatus={myEntriesStatus}
                      openEntryNameEditModal={openEntryNameEditModal}
                      fullContest={contestData}
                    />
                  ) : null}
                  {!isEntertainmentOnly && (
                    <PrizesSection
                      prizes={contestData.payout_schedule}
                      segments={contestData.settings?.segments}
                      isSidePanel={isSidePanel}
                      resizingMode={contestData.resizing_mode}
                      contestType={contestData.contest_type}
                      slateLabel={contestData.settings?.slateLabel}
                      contestTags={contestData.tags}
                    />
                  )}
                  <AboutSection
                    description={contestData.rich_short_rules || contestData.short_rules}
                    rosterRequirements={contestData.roster_requirements}
                    maxEntriesPerUser={maxEntriesPerUser}
                    slates={contestData.slates}
                    contestType={contestData.contest_type}
                    createdByAdmin={contestData.created_by_admin}
                    owner={getHandle(contestData.commissioner)}
                    ownerHref={Routes.commissionerChannel(contestData.commissioner.id)}
                    sport={contestData.league.sport}
                    isSidePanel={isSidePanel}
                    pickDeadlineType={contestData.settings?.pickDeadlineType}
                    entryDeadline={contestData.start_date}
                  />
                  <ScheduleSection
                    slates={contestData.slates}
                    segments={contestData.settings?.segments}
                    isSidePanel={isSidePanel}
                    slateLabel={contestData.settings?.slateLabel}
                  />
                  <RulesSection rules={contestData.rules} isSidePanel={isSidePanel} />
                  <LongRules longRules={contestData.long_rules} isSidePanel={isSidePanel} />
                </>
              ) : null}
              {user && isTabsShown && activeTab === ContentTab.ENTRANTS ? (
                <Section>
                  <SectionTitle isSidePanel={isSidePanel}>
                    {t('detail.entrants.title')}
                  </SectionTitle>
                  <SectionContent isSidePanel={isSidePanel}>
                    <Entrants contestId={contestData.id} />
                  </SectionContent>
                </Section>
              ) : null}
              {user && isTabsShown && activeTab === ContentTab.POSTS ? (
                <Section>
                  <Posts channel={channel} contestId={contestData.id} showCta smallText />
                </Section>
              ) : null}
            </Stack>
          </div>
          <aside className={classes.side}>
            {user && (
              <Posts
                channel={channel}
                contestId={contestData.id}
                showCta
                isInfiniteScroll={false}
              />
            )}
            {!hideEntries &&
              (user ? (
                <>
                  <SectionTitle>{t('detail.entrants.title')}</SectionTitle>
                  <Entrants contestId={contestData.id} />
                </>
              ) : (
                <NewToSplash />
              ))}
          </aside>
        </div>
      </section>
      <ConnectedSignUpModal />
    </>
  );
}

export default ContestDetail;
